import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './ui/Button';
import { CreditCard, ShoppingBag, AlertTriangle, Zap } from 'lucide-react';
import { useCurrencyContext } from '../hooks/CurrencyContext';
const POWER_HOOKS = ['¿Otra vez compraste algo que no necesitabas?', '¿Cuánto llevas gastado este mes sin darte cuenta?', '¿Te da miedo abrir tu app del banco?'];
export function Hero() {
  const [hookIndex, setHookIndex] = useState(0);
  const {
    formatPrice,
    formatPriceShort
  } = useCurrencyContext();
  const scrollToPricing = () => {
    document.getElementById('pricing')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  useEffect(() => {
    const interval = setInterval(() => {
      setHookIndex(prev => (prev + 1) % POWER_HOOKS.length);
    }, 3500);
    return () => clearInterval(interval);
  }, []);
  return <>
      {/* PROMO BANNER */}
      <motion.div initial={{
      opacity: 0,
      y: -20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.4
    }} className="w-full bg-gradient-to-r from-[#C4467E] via-[#A33668] to-[#C4467E] text-white px-4 py-5 md:py-6 relative overflow-hidden" data-id="element-77">
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent animate-pulse" data-id="element-78" />
        <div className="max-w-3xl mx-auto text-center relative" data-id="element-79">
          <div className="flex items-center justify-center gap-2 mb-2" data-id="element-80">
            <Zap className="w-5 h-5 fill-yellow-300 text-yellow-300" data-id="element-81" />
            <span className="text-xs md:text-sm font-bold uppercase tracking-widest text-yellow-200" data-id="element-82">
              Super Promo · Solo esta semana
            </span>
            <Zap className="w-5 h-5 fill-yellow-300 text-yellow-300" data-id="element-83" />
          </div>
          <p className="text-base md:text-lg font-bold leading-snug mb-3" data-id="element-84">
            🤯 ¡Nos volvimos locos! Como agradecimiento por tus compras, hemos
            bajado los precios de todos nuestros productos a tan solo{' '}
            <span className="line-through text-white/50 text-base" data-id="element-85">
              {formatPrice(47.99)}
            </span>{' '}
            <span className="text-yellow-200 text-xl md:text-2xl" data-id="element-86">
              {formatPrice(7.99)}
            </span>
          </p>
          <p className="text-white/80 text-xs md:text-sm mb-4" data-id="element-87">
            ⏰ Aprovecha, se acaba pronto
          </p>
          <button onClick={scrollToPricing} className="bg-white text-[#C4467E] font-bold text-sm md:text-base px-6 py-2.5 rounded-full hover:bg-yellow-100 transition-colors shadow-lg" data-id="element-88">
            ¡Quiero aprovechar! →
          </button>
        </div>
      </motion.div>

      <header className="w-full min-h-screen flex flex-col justify-center bg-[#FFF5F0]/50 px-6 py-12 md:py-24 relative overflow-hidden" data-id="element-89">
        <div className="max-w-6xl mx-auto w-full relative" data-id="element-90">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center" data-id="element-91">
            {/* LEFT */}
            <div className="space-y-7 text-left" data-id="element-92">
              <div className="h-16 md:h-20 flex items-center" data-id="element-93">
                <AnimatePresence mode="wait" data-id="element-94">
                  <motion.p key={hookIndex} initial={{
                  opacity: 0,
                  y: 12
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0,
                  y: -12
                }} transition={{
                  duration: 0.35
                }} className="text-lg md:text-xl text-[#C4467E] font-medium tracking-wide uppercase" data-id="element-95">
                    {POWER_HOOKS[hookIndex]}
                  </motion.p>
                </AnimatePresence>
              </div>

              <motion.h1 initial={{
              opacity: 0,
              y: 15
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.4
            }} className="text-4xl md:text-5xl lg:text-6xl font-serif text-[#3D1F2E] leading-[1.1] tracking-tight" data-id="element-96">
                Deja de tirar <br data-id="element-97" />
                <span className="italic text-[#C4467E]" data-id="element-98">tu dinero</span> <br data-id="element-99" />a
                la basura.
              </motion.h1>

              <motion.div initial={{
              opacity: 0,
              y: 15
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.4,
              delay: 0.1
            }} className="bg-[#3D1F2E]/90 rounded-2xl p-5 md:p-6 inline-block" data-id="element-100">
                <p className="text-[#E8D5DC] text-sm uppercase tracking-widest mb-1" data-id="element-101">
                  ¿Sabías que…?
                </p>
                <p className="text-white font-serif text-xl md:text-2xl font-bold leading-snug" data-id="element-102">
                  El 73% de las compras impulsivas <br data-id="element-103" />
                  <span className="text-[#C4467E]" data-id="element-104">
                    se hacen desde el celular, de noche.
                  </span>
                </p>
                <p className="text-[#7A5C6B] text-xs mt-2" data-id="element-105">
                  💡 Cuando estás cansada, tu cerebro busca dopamina — no
                  productos.
                </p>
              </motion.div>

              <motion.div initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              duration: 0.4,
              delay: 0.15
            }} className="flex flex-col gap-3" data-id="element-106">
                {[{
                icon: <ShoppingBag className="w-5 h-5" data-id="element-107" />,
                text: '"Solo voy a ver..." y terminas comprando'
              }, {
                icon: <CreditCard className="w-5 h-5" data-id="element-108" />,
                text: 'La culpa después del cargo. Cada. Vez.'
              }, {
                icon: <AlertTriangle className="w-5 h-5" data-id="element-109" />,
                text: '"Ahora sí me controlo" — hasta que vuelve a pasar'
              }].map((item, i) => <div key={i} className="flex items-center gap-3 text-[#7A5C6B]" data-id="element-110">
                    <div className="flex-shrink-0 w-9 h-9 rounded-full bg-[#FDE8F0]/80 flex items-center justify-center text-[#C4467E]" data-id="element-111">
                      {item.icon}
                    </div>
                    <span className="text-base md:text-lg" data-id="element-112">{item.text}</span>
                  </div>)}
              </motion.div>

              <motion.div initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.4,
              delay: 0.2
            }} className="flex flex-col sm:flex-row items-start gap-4 pt-2" data-id="element-113">
                <Button size="lg" withArrow onClick={scrollToPricing} data-id="element-114">
                  Quiero recuperar mi control
                </Button>
                <p className="text-xs text-[#7A5C6B] self-center" data-id="element-115">
                  Solo {formatPrice(7.99)} · Garantía 30 días
                </p>
              </motion.div>
            </div>

            {/* RIGHT */}
            <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} className="relative flex justify-center lg:justify-end" data-id="element-116">
              <div className="relative" data-id="element-117">
                <img src="https://images.unsplash.com/photo-1483985988355-763728e1935b?w=400&h=500&fit=crop&crop=center&q=75" alt="Mujer con bolsas de compras" className="w-full max-w-sm md:max-w-md rounded-3xl shadow-2xl object-cover" loading="eager" decoding="async" style={{
                aspectRatio: '3/4'
              }} data-id="element-118" />
                <div className="absolute inset-0 rounded-3xl bg-gradient-to-t from-[#3D1F2E]/60 via-transparent to-transparent" data-id="element-119" />

                <div className="absolute bottom-6 left-6 right-6" data-id="element-120">
                  <p className="text-white font-serif text-xl md:text-2xl italic leading-snug" data-id="element-121">
                    "No te falta disciplina. <br data-id="element-122" />
                    <span className="text-[#FDE8F0] font-bold not-italic" data-id="element-123">
                      Te falta un sistema."
                    </span>
                  </p>
                </div>

                <div className="absolute -bottom-8 -left-6 md:-left-12 bg-white/90 backdrop-blur-md rounded-2xl p-4 shadow-xl border border-[#E8D5DC]" style={{
                transform: 'rotate(-3deg)'
              }} data-id="element-124">
                  <img src="https://cdn.magicpatterns.com/uploads/h241stnUBcHgWd2SzzrFSw/Anti_impulso_cover.png" alt="Anti-Impulso — Ebook" className="w-28 md:w-36 rounded-lg" loading="eager" decoding="async" data-id="element-125" />
                  <p className="text-[#3D1F2E] font-bold text-sm mt-2 text-center" data-id="element-126">
                    Anti-Impulso
                  </p>
                  <div className="text-center" data-id="element-127">
                    <span className="text-[#7A5C6B] text-sm line-through" data-id="element-128">
                      {formatPrice(47.99)}
                    </span>
                    <p className="text-[#C4467E] font-serif text-lg font-bold" data-id="element-129">
                      {formatPrice(7.99)}
                    </p>
                  </div>
                </div>

                <div className="absolute top-6 -right-4 md:-right-8 bg-[#C4467E] text-white rounded-full px-4 py-2 shadow-lg" data-id="element-130">
                  <p className="text-xs font-bold uppercase tracking-wider" data-id="element-131">
                    🔥 14 días
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </header>
    </>;
}